import React, { useEffect, useState, useContext } from 'react';
import { Label, Toast, ToastHeader, ToastBody, CloseButton } from "reactstrap";
import { useIsAuthenticated, useMsal, useMsalAuthentication } from '@azure/msal-react';
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import useUser from '../../hooks/useUser';
import { InteractionStatus } from '@azure/msal-browser';
import InfoCircle from '../../components/Solution/assets/info-circle.svg';
import Styles from './Login.module.css';
import useSignUp from '../../hooks/useSignUp';
import { UserContext } from '../../context/User';
import { PixelContext } from '../../context/Pixel';
import useAuthState from '../../hooks/useAuthState';
import { APPConstants } from '../../constants/app';
import { RolesConstants } from '../../constants/roles';
import BaseLogin from './Login/Base';
import UploadPhotoLogin from './Login/UploadPhoto';

function Login() {
    // eslint-disable-next-line
    const LOGIN_ERROR_QUERY = 'loginError';

    const [waitCreateUser, setWaitCreateUser] = useState(false);
    const [handleRedirect, setHandleRedirect] = useState(false);
    const [loginError, setLoginError] = useState(false);
    const { inProgress, instance } = useMsal();
    const { result } = useMsalAuthentication();
    const { setUser } = useContext(UserContext);
    const { MetaPixel } = useContext(PixelContext);
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();
    const signUp = useSignUp();
    const user = useUser();
    const location = useLocation();
    const authState = useAuthState();
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
      if (inProgress === InteractionStatus.None && isAuthenticated) {
        // If we end up here and we're already authenticated, determine if local user
        // needs to be created and wait for it
        if (!waitCreateUser || user.user || user.error) {
          if (handleRedirect) {
            let userRole = authState.getRole() ? [authState.getRole()] : null;
            let proceed = true;
            user.createFromAccount(userRole).then((userData) => {
              if (userData) {
                setUser(userData);
                if (userData.userIsNew) {
                  MetaPixel.track('CompleteRegistration');
                }
              }
            }).catch((error) => {
              proceed = false;
              instance.logoutRedirect({postLogoutRedirectUri: window.location.href + "?" + LOGIN_ERROR_QUERY + "=409"});
            }).finally(() => {
              if (proceed) {
                redirectSuccess();
              }
            });
          }
          if (!handleRedirect) {
            redirectSuccess();
          }
        }
      } else if (inProgress !== InteractionStatus.None) {
        setLoginError(searchParams.has(LOGIN_ERROR_QUERY));
        setHandleRedirect(inProgress === InteractionStatus.HandleRedirect);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, navigate, inProgress, user.user]);

    const onSignInError = (error) => {
      console.log(error);
    }

    const redirectSuccess = (path) => {
      const callbackUrl = getCallbackUrl();
      const statePath = result?.state && !authState.getStateObject()
        ? result.state
        : (callbackUrl ? callbackUrl : null);
      const uri = path 
        ? path 
        : (statePath ? statePath : APPConstants.APP_BASE_URI);
      navigate(uri);
    }

    const handleLogin = () => {
      instance.loginRedirect({
        redirectUri: "/auth/login",
        redirectStartPage: "/auth/login",
        state: getCallbackUrl()
      }).catch(e => {
        onSignInError(e);
      });
    }

    const handleSignUp = () => {
      signUp.signupWithRole();
    }

    const handleHomeownerSignUp = () => {
      signUp.signupWithRole(RolesConstants.HOMEOWNER, getCallbackUrl());
    }

    const handleGoogleLogin = () => {
      instance.loginRedirect({
        redirectUri: "/auth/login",
        redirectStartPage: "/auth/login",
        state: getCallbackUrl(),
        extraQueryParameters: {
          domain_hint: "google.com",
        }
      }).catch((e) => {
        onSignInError(e);
      });
    };

    const getCallbackUrl = () => {
      return location.state?.callbackUrl
        ? location.state?.callbackUrl
        : null;
    }

    const shouldAllowContinueWithoutAccount = () => {
      const locationValue = location.state?.continueWithoutAccount;

      return locationValue !== undefined ? locationValue : true;
    }

    const showImageLogin = getCallbackUrl() !== null && getCallbackUrl().includes('upload-photo');

    return (
      (!isAuthenticated && inProgress === InteractionStatus.None) ?
      <>
      <div className="position-fixed top-0 start-0 end-0 p-3">
        <Toast isOpen={loginError} fade={true} className="text-white" style={{ backgroundColor: '#282828' }}>
          <img src={InfoCircle} alt="" className="text-start position-absolute ps-1 pt-1"/> 
          <ToastHeader className="text-white border-bottom-0" style={{ backgroundColor: '#282828' }} 
            close={<CloseButton onClick={() => setLoginError(!loginError)} className="btn-close-white" />}>
            Something went wrong
          </ToastHeader>
          <ToastBody>
            We couldn’t create your account because the email address is already registered. Please sign in with an existing account or register with a different email address.
          </ToastBody>
        </Toast>
      </div>
      {
        !showImageLogin &&
        <BaseLogin onHandleLogin={ handleLogin } onHandleSignUp={ handleSignUp } allowContinueWithoutAccount={ shouldAllowContinueWithoutAccount() }/>
      }
      {
          showImageLogin &&
          <UploadPhotoLogin onHandleLogin={ handleLogin } onHandleGoogleLogin={ handleGoogleLogin } onHandleHomeownerSignUp={ handleHomeownerSignUp } />
      }
      </>
      :
      <div className={Styles.background}>
        <Label className='text-white'>Please wait while we redirect you...</Label>
      </div>
    );
}

export default Login;
